import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { inject, observer } from 'mobx-react';
import { getSpaceUsedInUnit } from '../util';
import dayjs from 'dayjs';
import logo from '../assets/logo.png';

const C = {
  brand:  '#F777B0',
  brand2: '#D94F92',
  ink:    '#1A1614',
  ink2:   '#3D3632',
  ink3:   '#8A8079',
  paper:  '#FBF8F2',
  paper2: '#F4EFE3',
  hair:   '#E8E0D0',
  hair2:  '#D4CABA',
  serif:  '"Instrument Serif", "Songti SC", serif',
  sans:   '"Inter", "Noto Sans SC", -apple-system, sans-serif',
  mono:   '"JetBrains Mono", ui-monospace, monospace',
};

const NAV_GROUPS = [
  {
    label: '工作台',
    items: [
      { id: 'orders',   to: '/user/orders',     icon: 'orders',  label: '订单',   badgeKey: null },
      { id: 'notifications', to: '/user/notifications', icon: 'bell', label: '通知', badgeKey: 'unreadNotifications' },
      { id: 'trash',    to: '/user/recycleBin',  icon: 'trash',   label: '回收站', badgeKey: null },
    ],
  },
  {
    label: '账户',
    items: [
      { id: 'packages', to: '/user/packages',    icon: 'plan',    label: '套餐',   badgeKey: null },
      { id: 'settings', to: '/user/setting/watermark', icon: 'gear', label: '设置', badgeKey: null },
      { id: 'support',  to: '/user/support',     icon: 'support', label: '支持',   badgeKey: 'unreadReplies' },
      { id: 'me',       to: '/user/info',        icon: 'me',      label: '我的',   badgeKey: null },
    ],
  },
];

function Icon({ name, size = 16 }) {
  const s = { width: size, height: size, fill: 'none', stroke: 'currentColor', strokeWidth: 1.5, strokeLinecap: 'round', strokeLinejoin: 'round' };
  switch (name) {
    case 'orders':  return <svg viewBox="0 0 24 24" {...s}><path d="M5 6h14M5 12h14M5 18h9"/><circle cx="3.5" cy="6" r=".7" fill="currentColor" stroke="none"/><circle cx="3.5" cy="12" r=".7" fill="currentColor" stroke="none"/><circle cx="3.5" cy="18" r=".7" fill="currentColor" stroke="none"/></svg>;
    case 'bell':    return <svg viewBox="0 0 24 24" {...s}><path d="M18 9a6 6 0 0 0-12 0c0 7-3 7-3 7h18s-3 0-3-7M10 20a2 2 0 0 0 4 0"/></svg>;
    case 'trash':   return <svg viewBox="0 0 24 24" {...s}><path d="M4 7h16M9 7V4h6v3M6 7l1.2 13a1.5 1.5 0 0 0 1.5 1.4h6.6a1.5 1.5 0 0 0 1.5-1.4L18 7"/></svg>;
    case 'plan':    return <svg viewBox="0 0 24 24" {...s}><path d="M5 4h14v16l-7-3-7 3z"/></svg>;
    case 'gear':    return <svg viewBox="0 0 24 24" {...s}><circle cx="12" cy="12" r="3"/><path d="M12 3v2M12 19v2M3 12h2M19 12h2M5.6 5.6l1.4 1.4M17 17l1.4 1.4M5.6 18.4 7 17M17 7l1.4-1.4"/></svg>;
    case 'support': return <svg viewBox="0 0 24 24" {...s}><path d="M5 11a7 7 0 0 1 14 0v5a3 3 0 0 1-3 3h-1M5 11v4a2 2 0 0 0 2 2h1v-7H7a2 2 0 0 0-2 2zM19 11v6"/></svg>;
    case 'me':      return <svg viewBox="0 0 24 24" {...s}><circle cx="12" cy="8" r="4"/><path d="M4 21c0-4.4 3.6-8 8-8s8 3.6 8 8"/></svg>;
    case 'crown':   return <svg viewBox="0 0 24 24" {...s} strokeWidth="1.6"><path d="m4 8 4 3 4-6 4 6 4-3-1.5 10h-13z"/><circle cx="4" cy="8" r="1.2" fill="currentColor"/><circle cx="20" cy="8" r="1.2" fill="currentColor"/><circle cx="12" cy="5" r="1.2" fill="currentColor"/></svg>;
    default: return null;
  }
}

function LogoMark({ size = 30 }) {
  return <img src={logo} alt="" aria-hidden="true" style={{ width: size, height: size, objectFit: 'contain', flexShrink: 0 }}/>
}

const PLAN_TIERS = { 0: '试用', 1: '会员', 2: '高级会员' };

const Sidebar = inject('userStore', 'chargeStore')(observer(({ userStore, chargeStore }) => {
  const location = useLocation();

  const storageUsed = getSpaceUsedInUnit(userStore.spaceUsed);
  const storageTotal = getSpaceUsedInUnit(userStore.spaceLimit);
  const storagePercent = userStore.spaceLimit > 0
    ? Math.min(100, Math.round((userStore.spaceUsed / userStore.spaceLimit) * 100))
    : 0;
  const tier = PLAN_TIERS[userStore.level] || '试用';
  const isPremium = Number(userStore.level) === 2;
  const expires = userStore.subscriptionEndDate
    ? dayjs(userStore.subscriptionEndDate).format('YYYY·MM·DD')
    : null;

  return (
    <aside style={{
      borderRight: `1px solid ${C.hair}`,
      background: C.paper2,
      display: 'flex', flexDirection: 'column',
      overflow: 'hidden',
      width: 232,
      flexShrink: 0,
    }}>
      {/* brand */}
      <div style={{ padding: '20px 22px 14px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 11 }}>
          <LogoMark size={48}/>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
            <span style={{ fontFamily: C.serif, fontSize: 28, lineHeight: 1, color: C.ink }}>图派</span>
            <span style={{
              fontSize: 9, fontWeight: 700, letterSpacing: 0.9,
              padding: '2px 5px', borderRadius: 3,
              background: C.brand, color: '#fff',
            }}>PRO</span>
          </div>
        </div>
      </div>

      {/* nav */}
      <nav style={{ flex: 1, padding: '4px 12px', overflow: 'auto' }}>
        {NAV_GROUPS.map((g, gi) => (
          <div key={g.label} style={{ marginBottom: 18, marginTop: gi === 0 ? 4 : 0 }}>
            <div style={{
              fontFamily: C.mono, fontSize: 10, letterSpacing: 1.6,
              color: C.ink3, textTransform: 'uppercase',
              padding: '4px 10px 8px',
            }}>
              {g.label}
            </div>
            {g.items.map(it => {
              const active = location.pathname.startsWith(it.to);
              const badge = it.badgeKey ? userStore[it.badgeKey] : null;
              return (
                <Link
                  key={it.id}
                  data-testid={`nav-${it.id}`}
                  to={it.to}
                  style={{
                    width: '100%',
                    display: 'grid',
                    gridTemplateColumns: '20px 1fr auto',
                    alignItems: 'center',
                    gap: 12,
                    padding: '9px 10px',
                    marginBottom: 1,
                    border: 'none',
                    background: active ? C.paper : 'transparent',
                    borderRadius: 8,
                    boxShadow: active ? `inset 0 0 0 1px ${C.hair2}` : 'none',
                    cursor: 'pointer', textAlign: 'left',
                    color: active ? C.ink : C.ink2,
                    fontFamily: C.sans,
                    textDecoration: 'none',
                    position: 'relative',
                  }}
                >
                  {active && <span style={{
                    position: 'absolute', left: -12, top: 11, bottom: 11, width: 2,
                    background: C.brand, borderRadius: 1,
                  }}/>}
                  <span style={{ color: active ? C.brand : C.ink3 }}><Icon name={it.icon} size={16}/></span>
                  <span style={{ fontSize: 14, fontWeight: active ? 600 : 500 }}>{it.label}</span>
                  {badge > 0 && (
                    <span data-testid={`nav-${it.id}-badge`} style={{
                      fontSize: 11, fontFamily: C.mono,
                      padding: '1px 6px', borderRadius: 8,
                      background: active ? C.brand : C.hair,
                      color: active ? '#fff' : C.ink2,
                      fontVariantNumeric: 'tabular-nums',
                    }}>
                      {badge}
                    </span>
                  )}
                </Link>
              );
            })}
          </div>
        ))}
      </nav>

      {/* membership card */}
      <div style={{ padding: '10px 14px 14px' }}>
        <div style={{
          position: 'relative',
          padding: '14px 14px 12px',
          background: isPremium
            ? 'linear-gradient(135deg, #7A5812 0%, #B88922 48%, #E2BD63 100%)'
            : 'linear-gradient(135deg, #1A1614 0%, #2B2320 100%)',
          color: isPremium ? '#FFF9E9' : '#F4ECDF', borderRadius: 12,
          overflow: 'hidden',
          boxShadow: isPremium ? '0 12px 28px -16px rgba(122,88,18,.8)' : 'none',
        }}>
          <div style={{
            position: 'absolute', inset: 0, opacity: isPremium ? 0.72 : 0.5,
            background: isPremium
              ? 'radial-gradient(ellipse at 85% 0%, rgba(255,249,220,.75), transparent 58%)'
              : `radial-gradient(ellipse at 85% 0%, ${C.brand}99, transparent 60%)`,
          }}/>
          <div style={{ position: 'relative' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
              <span style={{ color: isPremium ? '#FFF1B8' : C.brand }}><Icon name="crown" size={14}/></span>
              <span style={{
                fontFamily: C.mono, fontSize: 10, letterSpacing: 1.4, textTransform: 'uppercase',
                color: 'rgba(255,255,255,.65)',
              }}>{isPremium ? 'PICSECT · 高级会员' : `Member · ${tier}`}</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 8 }}>
              <span style={{ fontFamily: C.mono, fontSize: 11, color: 'rgba(255,255,255,.55)' }}>已用</span>
              <span style={{ fontFamily: C.mono, fontSize: 11, fontVariantNumeric: 'tabular-nums' }}>
                <span style={{ color: '#fff', fontWeight: 600 }}>{storageUsed}</span>
                <span style={{ color: 'rgba(255,255,255,.45)' }}> / {storageTotal}</span>
              </span>
            </div>
            <div style={{ height: 4, background: 'rgba(255,255,255,.12)', borderRadius: 2, overflow: 'hidden', marginBottom: 10 }}>
              <div style={{
                height: '100%', width: `${storagePercent}%`,
                background: `linear-gradient(90deg, ${C.brand}, #F4A8C8)`,
                borderRadius: 2,
              }}/>
            </div>
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              fontFamily: C.mono, fontSize: 10, color: 'rgba(255,255,255,.5)',
            }}>
              <span>{expires ? `到期 ${expires}` : '未订阅'}</span>
              <button data-testid="open-subscription-dialog" onClick={() => chargeStore.openChargeDialog()} style={{
                background: 'rgba(255,255,255,.08)', border: '1px solid rgba(255,255,255,.12)',
                color: '#fff', padding: '3px 8px', borderRadius: 4,
                fontFamily: C.mono, fontSize: 10, letterSpacing: 0.4,
                cursor: 'pointer',
              }}>
                续费 →
              </button>
            </div>
          </div>
        </div>
      </div>
    </aside>
  );
}));

export default Sidebar;
